/* ============================================================================
 * Mapas de apresentação compartilhados pelas telas de DOCUMENTOS FISCAIS.
 *
 * Os rótulos derivam dos enums REAIS do backend:
 *   - FiscalDocumentStatus (fiscal.prisma): DRAFT, QUEUED, PROCESSING,
 *     AUTHORIZED, REJECTED, DENIED, CANCELLED, CONTINGENCY_PENDING
 *   - SalesOrderStatus (sales.prisma): DRAFT, CONFIRMED, PARTIALLY_FULFILLED,
 *     FULFILLED, INVOICED, CLOSED, CANCELLED
 *   - FiscalEvent.type (String livre) — reconhecidos pela tela de eventos:
 *     CARTA_CORRECAO, CANCELAMENTO, MANIFESTACAO_* (fiscal-events.controller.ts
 *     EVENT_TYPES).
 * ========================================================================== */

import type { BadgeProps } from '@nexora/design-system';

type BadgeVariant = NonNullable<BadgeProps['variant']>;

/** Status do documento fiscal: rótulo pt-BR. */
export const DOCUMENT_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Rascunho',
  QUEUED: 'Na fila',
  PROCESSING: 'Processando',
  AUTHORIZED: 'Autorizada',
  REJECTED: 'Rejeitada',
  DENIED: 'Denegada',
  CANCELLED: 'Cancelada',
  CONTINGENCY_PENDING: 'Contingência',
};

export const DOCUMENT_STATUS_VARIANT: Record<string, BadgeVariant> = {
  DRAFT: 'neutral',
  QUEUED: 'info',
  PROCESSING: 'info',
  AUTHORIZED: 'success',
  REJECTED: 'error',
  DENIED: 'error',
  CANCELLED: 'neutral',
  CONTINGENCY_PENDING: 'warning',
};

export const DOCUMENT_STATUS_ICON: Record<string, string> = {
  DRAFT: 'edit_note',
  QUEUED: 'schedule',
  PROCESSING: 'cached',
  AUTHORIZED: 'check_circle',
  REJECTED: 'error',
  DENIED: 'gpp_bad',
  CANCELLED: 'cancel',
  CONTINGENCY_PENDING: 'cloud_off',
};

export function documentStatusLabel(status: string | null | undefined): string {
  return (status && DOCUMENT_STATUS_LABEL[status]) || status || '—';
}

export function documentStatusVariant(status: string | null | undefined): BadgeVariant {
  return (status && DOCUMENT_STATUS_VARIANT[status]) || 'neutral';
}

export function documentStatusIcon(status: string | null | undefined): string {
  return (status && DOCUMENT_STATUS_ICON[status]) || 'help';
}

/** Status do pedido no pipeline NFS-e (SalesOrderStatus): pronto p/ faturar quando CONFIRMED. */
export const PIPELINE_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Rascunho',
  CONFIRMED: 'Pronto p/ faturar',
  PARTIALLY_FULFILLED: 'Parcialmente atendido',
  FULFILLED: 'Atendido',
  INVOICED: 'Faturado',
  CLOSED: 'Encerrado',
  CANCELLED: 'Cancelado',
};

export const PIPELINE_STATUS_VARIANT: Record<string, BadgeVariant> = {
  DRAFT: 'neutral',
  CONFIRMED: 'success',
  PARTIALLY_FULFILLED: 'info',
  FULFILLED: 'info',
  INVOICED: 'success',
  CLOSED: 'neutral',
  CANCELLED: 'error',
};

/** Tipo de evento fiscal → rótulo + ícone (FiscalEvent.type é String livre). */
export function eventTypeLabel(type: string | null | undefined): string {
  const t = type ?? '';
  if (t === 'CARTA_CORRECAO') return 'Carta de Correção (CC-e)';
  if (t === 'CANCELAMENTO') return 'Cancelamento';
  if (t.startsWith('MANIFESTACAO')) return 'Manifestação do destinatário';
  return t || '—';
}

export function eventTypeIcon(type: string | null | undefined): string {
  const t = type ?? '';
  if (t === 'CARTA_CORRECAO') return 'edit_document';
  if (t === 'CANCELAMENTO') return 'cancel';
  if (t.startsWith('MANIFESTACAO')) return 'assignment_turned_in';
  return 'event_note';
}

/** Abrevia a chave de acesso (44 dígitos) para exibição compacta em tabela. */
export function shortAccessKey(accessKey: string | null | undefined): string {
  if (!accessKey) return '—';
  const digits = accessKey.replace(/\D/g, '');
  if (digits.length !== 44) return accessKey;
  return `${digits.slice(0, 4)}…${digits.slice(-6)}`;
}